import { useState } from 'react';
import { searchPostcode } from '@/utils/postcode';

interface AddressSearchProps {
  zipcode: string;
  address1: string;
  onSelect: (value: { zipcode: string; address1: string }) => void;
  /** 주소를 고른 뒤 포커스를 옮길 상세 주소 입력창의 id. */
  detailInputId?: string;
  /** 같은 화면에 두 번 쓸 때 id 충돌을 피하기 위한 접두어. */
  idPrefix?: string;
  required?: boolean;
}

/**
 * 우편번호 · 주소 두 필드를 카카오 우편번호 검색으로 채우는 묶음.
 *
 * <p>두 입력창은 읽기 전용이며 "주소 찾기" 버튼으로만 값이 들어간다.
 * 손으로 친 오타·누락을 막기 위한 것이므로 상세 주소는 이 컴포넌트 밖에서 따로 받는다.
 *
 * <p>읽기 전용 입력은 브라우저가 `required` 검사를 건너뛰므로, 비어 있는지는
 * 제출하는 쪽에서 utils/postcode.ts 의 `isAddressFilled` 로 확인해야 한다.
 */
export function AddressSearch({
  zipcode,
  address1,
  onSelect,
  detailInputId,
  idPrefix = '',
  required = false,
}: AddressSearchProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const zipcodeId = `${idPrefix}zipcode`;
  const address1Id = `${idPrefix}address1`;

  const handleSearch = async () => {
    setError(null);
    setBusy(true);
    try {
      // 팝업이 뜨면 바로 버튼을 되돌린다. 팝업이 뒤로 숨어도 다시 눌러 앞으로 불러올 수 있다.
      const picked = await searchPostcode(() => setBusy(false));
      if (picked) {
        onSelect(picked);
        if (detailInputId) document.getElementById(detailInputId)?.focus();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '우편번호 검색에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="field">
        <label htmlFor={zipcodeId}>우편번호{required && ' *'}</label>
        <div className="address-search__row">
          <input
            id={zipcodeId}
            className="input"
            required={required}
            readOnly
            placeholder="주소 찾기로 입력"
            value={zipcode}
            onClick={handleSearch}
          />
          <button type="button" className="btn btn--outline" onClick={handleSearch} disabled={busy}>
            {busy ? '여는 중…' : '주소 찾기'}
          </button>
        </div>
        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="field">
        <label htmlFor={address1Id}>주소{required && ' *'}</label>
        <input
          id={address1Id}
          className="input"
          required={required}
          readOnly
          placeholder="주소 찾기로 입력"
          value={address1}
          onClick={handleSearch}
        />
      </div>
    </>
  );
}
